import React, { useState } from 'react';
import { Button } from './ui/button';
import { Mail, Linkedin, Github, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import '../styles/Contact.css';

const Contact = ({ data }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Formspree endpoint from environment variable
      // To set up: Sign up at https://formspree.io, create a form, and update .env with your Form ID
      const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xpqjeoow';      
      
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          message: formData.message
        }).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        
        toast({
          title: "Message Sent! ✅",
          description: "Thank you for reaching out. I'll get back to you soon!",
          duration: 5000,
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      
      toast({
        title: "Error Sending Message ❌",
        description: "Something went wrong. Please try emailing me directly.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="contact-container">
        <div className="section-header">
          <h2 className="section-title">Let's Work Together</h2>
          <p className="section-subtitle">
            Ready to turn your data into actionable insights? Let's discuss your project
          </p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <h3 className="contact-info-title">Get in Touch</h3>
            <p className="contact-info-text">
              I'm currently available for freelance projects and full-time opportunities. 
              Whether you need help with data analysis, machine learning, or building dashboards, 
              I'd love to hear from you.
            </p>

            <div className="contact-links">
              <a href={`mailto:${data.email}`} className="contact-link">
                <div className="contact-link-icon">
                  <Mail className="h-5 w-5" />
                </div>
                <div className="contact-link-content">
                  <div className="contact-link-label">Email</div>
                  <div className="contact-link-value">{data.email}</div>
                </div>
              </a>

              <a href={data.linkedin} target="_blank" rel="noopener noreferrer" className="contact-link">
                <div className="contact-link-icon">
                  <Linkedin className="h-5 w-5" />
                </div>
                <div className="contact-link-content">
                  <div className="contact-link-label">LinkedIn</div>
                  <div className="contact-link-value">Connect with me</div>
                </div>
              </a>

              <a href={data.github} target="_blank" rel="noopener noreferrer" className="contact-link">
                <div className="contact-link-icon">
                  <Github className="h-5 w-5" />
                </div>
                <div className="contact-link-content">
                  <div className="contact-link-label">GitHub</div>
                  <div className="contact-link-value">View my work</div>
                </div>
              </a>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            {submitStatus === 'success' && (
              <div className="success-message">
                <CheckCircle2 className="h-5 w-5" />
                <span>Message sent successfully! I'll get back to you soon.</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="name" className="form-label">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-input"
                placeholder="Your name"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="your.email@example.com"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="form-textarea"
                placeholder="Tell me about your project or opportunity..."
                rows="5"
                required
                disabled={isSubmitting}
              />
            </div>

            <Button 
              type="submit" 
              size="lg" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
